import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { MESSAGE_TEMPLATES } from "@/components/landing/form/MessageTemplates";
import { supabase } from "@/integrations/supabase/client";

export const useAddLinkForm = (onSubmit: (linkData: any) => Promise<boolean>) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [whatsappNumber, setWhatsappNumber] = useState("");
  const [messageTemplate, setMessageTemplate] = useState(MESSAGE_TEMPLATES.default);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const checkTitleAvailability = async (title: string) => {
    const { data, error } = await supabase
      .from("marketplace_links")
      .select("title")
      .eq("title", title)
      .maybeSingle();
    
    if (error) {
      console.error("Error checking title availability:", error);
      return false;
    }
    
    return data === null; // Title is available if no data is returned
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !whatsappNumber) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Mohon isi semua field yang diperlukan",
      });
      return;
    }

    if (title.length < 5) {
      toast({
        variant: "destructive",
        title: "Link terlalu pendek",
        description: "Nama produk harus minimal 5 karakter",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Check if title is available before submitting
      const isTitleAvailable = await checkTitleAvailability(title);
      if (!isTitleAvailable) {
        toast({
          variant: "destructive",
          title: "Nama produk sudah digunakan",
          description: "Silakan gunakan nama produk yang lain",
        });
        setIsLoading(false);
        return;
      }

      const success = await onSubmit({
        url: "",
        platform: "whatsapp",
        title,
        description, // Make sure description is included here
        whatsapp_number: whatsappNumber,
        message_template: messageTemplate,
      });

      if (success) {
        setIsSuccess(true);
        setTimeout(() => setIsSuccess(false), 1000);
        setTitle("");
        setDescription("");
        setWhatsappNumber("");
        setMessageTemplate(MESSAGE_TEMPLATES.default);
        toast({
          title: "✨ Berhasil!",
          description: "Link produk berhasil dibuat",
        });
      }
    } catch (error: any) {
      console.error("Error submitting form:", error);
      const errorBody = error.body ? JSON.parse(error.body) : {};
      if (errorBody.code === "23505" || error.message?.includes("unique_title")) {
        toast({
          variant: "destructive",
          title: "Nama produk sudah digunakan",
          description: "Silakan gunakan nama produk yang lain",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Terjadi kesalahan. Silakan coba lagi.",
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return {
    title,
    setTitle,
    description,
    setDescription,
    whatsappNumber,
    setWhatsappNumber,
    messageTemplate,
    setMessageTemplate,
    isLoading,
    isSuccess,
    handleSubmit,
  };
};